import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import './ImageGallery.css';

interface MediaData {
  stage: string;
  view: string;
  instanceUID: string;
  mediaUrl: string;
}

interface MediaItem {
  src: string;
  type: 'image' | 'video';
  originalData?: MediaData;
}

interface ImageGalleryProps {
  leftImages: MediaData[];
  rightImages: MediaData[];
  onVideoClick?: (videoUrl: string, videoList: string[], index: number) => void;
  isPlaying?: boolean;
  openReportPanel?: boolean;
  /** Left/right arrow: move to the previous (-1) or next (1) view. */
  onStepView?: (dir: 1 | -1) => void;
  /** False while an overlay (clip popup, compare, overview) owns the arrow keys. */
  keyboardEnabled?: boolean;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  leftImages,
  rightImages,
  onVideoClick,
  isPlaying,
  openReportPanel,
  onStepView,
  keyboardEnabled = true,
}) => {
  const [selectedLeftIndex, setSelectedLeftIndex] = useState(0);
  const [selectedRightIndex, setSelectedRightIndex] = useState(0);
  // The pane the keyboard acts on. Rest leads by default so the arrows work the moment
  // the screen opens, without the cardiologist having to click a pane first.
  const [activeSide, setActiveSide] = useState<'left' | 'right'>('left');

  // Refs for controlling video playback
  const leftVideoRef = useRef<HTMLVideoElement | null>(null);
  const rightVideoRef = useRef<HTMLVideoElement | null>(null);

  // Effect to play/pause video based on isPlaying prop
  useEffect(() => {
    if (leftVideoRef.current) {
      isPlaying ? leftVideoRef.current.play() : leftVideoRef.current.pause();
    }
    if (rightVideoRef.current) {
      isPlaying ? rightVideoRef.current.play() : rightVideoRef.current.pause();
    }
  }, [isPlaying]);

  /**
   * Selecting a different view hands us a different set of clips. Without this, a pane
   * left on clip 3 of a three-clip view would point past the end of a one-clip view and
   * render nothing at all.
   */
  useEffect(() => {
    setSelectedLeftIndex(i => (i < (leftImages?.length || 0) ? i : 0));
  }, [leftImages]);

  useEffect(() => {
    setSelectedRightIndex(i => (i < (rightImages?.length || 0) ? i : 0));
  }, [rightImages]);

  /**
   * Step one pane through its own clips, wrapping at either end.
   *
   * Until now the stacked corner thumbnails were the ONLY way to change clip — a ~1cm
   * target overlapping the image, hit repeatedly while reading a stress study. Dr Shashi
   * asked for the arrows the quad view already has (`handleInnerNavClick` there), or for
   * the keyboard to do it.
   */
  const stepSide = useCallback(
    (side: 'left' | 'right', dir: 1 | -1) => {
      const total = (side === 'left' ? leftImages?.length : rightImages?.length) || 0;
      if (total < 2) {
        return;
      }
      const setter = side === 'left' ? setSelectedLeftIndex : setSelectedRightIndex;
      setter(cur => (cur + dir + total) % total);
    },
    [leftImages, rightImages]
  );

  /**
   * With onStepView: up/down walk the Views list (it runs top to bottom); left/right step
   * the clips in every pane that has more than one, like clicking the corner thumbnails.
   * Without it, left/right step the active pane's clips and up/down swap panes.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!keyboardEnabled) {
        return;
      }
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        return;
      }
      if (onStepView) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          onStepView(e.key === 'ArrowDown' ? 1 : -1);
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const dir = e.key === 'ArrowRight' ? 1 : -1;
          stepSide('left', dir);
          stepSide('right', dir);
        }
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        stepSide(activeSide, e.key === 'ArrowRight' ? 1 : -1);
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSide(s => (s === 'left' ? 'right' : 'left'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeSide, stepSide, onStepView, keyboardEnabled]);

  // Function to normalize media items
  const normalizeMediaItem = (item: MediaData): MediaItem => {
    // Detect if it's an image or video based on file extension in mediaUrl
    const isVideo = /\.webm$/i.test(item.mediaUrl);

    return {
      src: item?.mediaUrl,
      type: isVideo ? 'video' : 'image',
      originalData: item,
    };
  };

  // Normalize the arrays
  const normalizedLeftMedia = leftImages?.map(normalizeMediaItem);
  const normalizedRightMedia = rightImages?.map(normalizeMediaItem);

  // Function to render thumbnail
  const renderThumbnail = (item: MediaItem, index: number, side: 'left' | 'right') => {
    const isSelected = side === 'left' ? selectedLeftIndex === index : selectedRightIndex === index;
    const setSelected = side === 'left' ? setSelectedLeftIndex : setSelectedRightIndex;
    const data = item?.originalData;

    return (
      <div
        key={`${side}-${index}`}
        className={`thumbnail ${isSelected ? 'active' : ''}`}
        onClick={() => setSelected(index)}
        title={data ? `${data.stage} - ${data.view}` : ''}
      >
        {item?.type === 'image' ? (
          <img
            src={item?.src}
            alt={`${side} thumbnail ${index}`}
          />
        ) : (
          <div className="video-thumbnail">
            <video
              src={item?.src}
              loop
            />
            <div className="play-icon">▶</div>
          </div>
        )}
      </div>
    );
  };

  // Function to render main media
  const renderMainMedia = (
    item: MediaItem,
    side: 'left' | 'right',
    index: number,
    mediaList: MediaItem[]
  ) => {
    const data = item?.originalData;
    const stage = data?.stage === 'BASELINE' || data?.stage === 'Baseline' ? 'REST' : data?.stage;

    // Extract video URLs from the media list
    const videoList = mediaList?.filter(media => media?.type === 'video').map(media => media?.src);
    const videoIndex = videoList?.indexOf(item?.src); // Find the index of the clicked video

    return (
      <div
        className={`media-wrapper ${openReportPanel ? 'report-open' : ''}`}
        onDoubleClick={() => onVideoClick(item.src, videoList, videoIndex)}
      >
        {item?.type === 'image' ? (
          <img
            src={item?.src}
            alt={`${side} main`}
            className="main-media"
          />
        ) : (
          <video
            ref={side === 'left' ? leftVideoRef : rightVideoRef}
            src={item?.src}
            width="100%"
            className="main-media"
            controls
            autoPlay
            playsInline
            loop
            disablePictureInPicture
            controlsList="nofullscreen"
          />
        )}
        {/* {data && (
          <div className="media-info">
            {stage} - {data?.view}
          </div>
        )} */}
      </div>
    );
  };

  /** Per-pane clip counter (e.g. 2/5). Clips change with the left/right keys or the thumbnails. */
  const renderPaneControls = (side: 'left' | 'right', current: number, total: number) => {
    if (total < 2) {
      return null;
    }

    return (
      <>
        <Typography
          sx={{
            // Top, not bottom: the CSS reserves the lower 60px of this container for the
            // native video controls, so a counter down there would sit on the scrub bar.
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 11,
            px: 1,
            py: 0.25,
            borderRadius: 5,
            fontSize: 11.5,
            fontWeight: 600,
            color: '#cbd5e1',
            bgcolor: 'rgba(6,18,34,0.62)',
            pointerEvents: 'none',
          }}
        >
          {current + 1}/{total}
        </Typography>
      </>
    );
  };

  const paneOutline = (side: 'left' | 'right') => ({
    position: 'relative',
    height: '100%',
    // Only mark the active pane when both are on screen, and only once there is
    // something to step through — otherwise the ring is decoration.
    outline:
      activeSide === side &&
      normalizedLeftMedia.length > 0 &&
      normalizedRightMedia.length > 0 &&
      (side === 'left' ? normalizedLeftMedia.length : normalizedRightMedia.length) > 1
        ? '2px solid #12B5C0'
        : 'none',
    outlineOffset: '-2px',
    borderRadius: '10px',
  });

  return (
    <div className={`gallery-container ${openReportPanel ? 'report-open' : ''}`}>
      {!openReportPanel && <div className="gallery-divider"></div>}
      <div className="gallery-side">
        {normalizedLeftMedia.length > 0 && (
          <Box
            className="main-media-container"
            onMouseDown={() => setActiveSide('left')}
            sx={paneOutline('left')}
          >
            <div className="thumbnails-container left-thumbnails">
              {normalizedLeftMedia?.map((item, index) => renderThumbnail(item, index, 'left'))}
            </div>
            {renderMainMedia(
              normalizedLeftMedia[selectedLeftIndex],
              'left',
              selectedLeftIndex,
              normalizedLeftMedia
            )}
            {renderPaneControls('left', selectedLeftIndex, normalizedLeftMedia.length)}
          </Box>
        )}
      </div>

      <div className="gallery-side">
        {normalizedRightMedia.length > 0 && (
          <Box
            className="main-media-container"
            onMouseDown={() => setActiveSide('right')}
            sx={paneOutline('right')}
          >
            <div className="thumbnails-container right-thumbnails">
              {normalizedRightMedia?.map((item, index) => renderThumbnail(item, index, 'right'))}
            </div>
            {renderMainMedia(
              normalizedRightMedia[selectedRightIndex],
              'right',
              selectedRightIndex,
              normalizedRightMedia
            )}
            {renderPaneControls('right', selectedRightIndex, normalizedRightMedia.length)}
          </Box>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
